/**
 * RenderSchedulerConfig -- callbacks the scheduler needs from the engine.
 *
 * - `onFrame`        : per-frame work (animation manager tick, camera update, debug helpers)
 * - `render`         : the actual `renderer.render(scene, camera)` call
 * - `getDelta`       : returns delta-time from the scene Clock
 * - `shouldContinue` : returns true when the continuous loop still has work
 *                      (e.g. animations running, camera damping)
 */
export type RenderSchedulerConfig = {
	onFrame: (delta: number) => void;
	render: () => void;
	getDelta: () => number;
	shouldContinue: () => boolean;
};

/**
 * RenderScheduler replaces the unconditional requestAnimationFrame loop with
 * a demand-driven rendering model.
 *
 * Two modes of operation:
 *
 * 1. **Single-frame render** -- `requestRender()` coalesces multiple calls
 *    within a single browser frame into exactly one `render()` call.
 *
 * 2. **Continuous loop** -- `startLoop()` begins a rAF loop that calls
 *    `onFrame(delta)` + `render()` every frame.  The loop self-terminates
 *    when `shouldContinue()` returns false (no animations, camera idle).
 *    External code can also call `stopLoop()` explicitly.
 *
 * @example
 * ```typescript
 * const scheduler = new RenderScheduler({
 *   onFrame: (delta) => engine.onFrame(delta),
 *   render: () => sceneManager.render(),
 *   getDelta: () => clock.getDelta(),
 *   shouldContinue: () => animationManager.count > 0
 * });
 *
 * // Trigger a single re-render after a texture swap
 * scheduler.requestRender();
 *
 * // Start continuous loop during camera interaction
 * scheduler.startLoop();
 * ```
 */
export class RenderScheduler {
	#config: RenderSchedulerConfig;

	/** True while a continuous rAF loop is active */
	#isLoopRunning = false;

	/** rAF handle for the continuous loop (null when idle) */
	#loopFrameId: number | null = null;

	/** True when a single-frame render has been scheduled but not yet executed */
	#needsRender = false;

	/** rAF handle for a single-frame render (null when idle) */
	#singleFrameId: number | null = null;

	/** True after dispose() -- prevents further scheduling */
	#isDisposed = false;

	constructor (config: RenderSchedulerConfig) {
		this.#config = config;
	}

	// ─────────────────────────────────────────────────────────────────────────
	// Public getters
	// ─────────────────────────────────────────────────────────────────────────

	/**
	 * Whether the continuous render loop is currently running
	 */
	get isLoopRunning (): boolean {
		return this.#isLoopRunning;
	}

	// ─────────────────────────────────────────────────────────────────────────
	// Single-frame render
	// ─────────────────────────────────────────────────────────────────────────

	/**
	 * Request a single re-render on the next animation frame.
	 * Multiple calls within the same frame are coalesced into one render.
	 * If the continuous loop is already running, this is a no-op because
	 * the loop will render on the next frame anyway.
	 */
	requestRender (): void {
		if (this.#isDisposed) return;

		// If the continuous loop is active it will render on its own
		if (this.#isLoopRunning) return;

		// Already scheduled -- coalesce
		if (this.#needsRender) return;

		this.#needsRender = true;
		this.#singleFrameId = requestAnimationFrame(() => {
			this.#singleFrameId = null;
			this.#needsRender = false;

			if (this.#isDisposed) return;

			const { onFrame, render, getDelta } = this.#config;
			const delta = getDelta();
			onFrame(delta);
			render();
		});
	}

	// ─────────────────────────────────────────────────────────────────────────
	// Continuous loop
	// ─────────────────────────────────────────────────────────────────────────

	/**
	 * Start a continuous render loop.
	 * The loop calls `onFrame(delta)` + `render()` every frame and
	 * self-terminates when `shouldContinue()` returns false.
	 *
	 * Calling `startLoop()` while the loop is already running is a no-op.
	 */
	startLoop (): void {
		if (this.#isDisposed) return;
		if (this.#isLoopRunning) return;

		// Cancel any pending single-frame render -- the loop will cover it
		this.#cancelSingleFrame();

		this.#isLoopRunning = true;
		this.#scheduleLoopFrame();
	}

	/**
	 * Stop the continuous render loop.
	 */
	stopLoop (): void {
		this.#isLoopRunning = false;

		if (this.#loopFrameId !== null) {
			cancelAnimationFrame(this.#loopFrameId);
			this.#loopFrameId = null;
		}
	}

	// ─────────────────────────────────────────────────────────────────────────
	// Lifecycle
	// ─────────────────────────────────────────────────────────────────────────

	/**
	 * Dispose of the scheduler -- cancels any pending frames and prevents
	 * future scheduling.
	 */
	dispose (): void {
		this.#isDisposed = true;
		this.stopLoop();
		this.#cancelSingleFrame();
	}

	// ─────────────────────────────────────────────────────────────────────────
	// Private helpers
	// ─────────────────────────────────────────────────────────────────────────

	/**
	 * Schedule the next loop frame via rAF.
	 */
	#scheduleLoopFrame (): void {
		this.#loopFrameId = requestAnimationFrame(() => {
			this.#loopFrameId = null;

			if (this.#isDisposed || !this.#isLoopRunning) return;

			const { onFrame, render, getDelta, shouldContinue } = this.#config;
			const delta = getDelta();

			onFrame(delta);
			render();

			// Self-terminate when there is no more work
			if (!shouldContinue()) {
				this.#isLoopRunning = false;
				return;
			}

			// Schedule the next frame
			this.#scheduleLoopFrame();
		});
	}

	/**
	 * Cancel a pending single-frame render request.
	 */
	#cancelSingleFrame (): void {
		if (this.#singleFrameId !== null) {
			cancelAnimationFrame(this.#singleFrameId);
			this.#singleFrameId = null;
		}
		this.#needsRender = false;
	}
}
